import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './Home.module.css'

export function Home() {
  const [animationOffset, setAnimationOffset] = useState(0)
  const [howTab, setHowTab] = useState<'student' | 'instructor'>('student')

  useEffect(() => {
    // Simple React-based animation
    const interval = setInterval(() => {
      setAnimationOffset(prev => (prev + 1) % 100)
    }, 100)

    const cleanupInterval = () => clearInterval(interval)
    // Simple DOM-based matrix rain for additional effect
    const createDOMMatrixRain = () => {
      const matrix = document.getElementById('matrix')
      if (!matrix) return

      const characters = '01ABCDEF{}[]()<>+-*/=POTATO🥔⚓🌊'
      const columns = Math.floor(window.innerWidth / 20)
      
      // Clear existing columns
      matrix.innerHTML = ''
      
      for (let i = 0; i < columns; i++) {
        const column = document.createElement('div')
        column.className = styles.matrixColumn
        column.style.left = i * 20 + 'px'
        column.style.animationDuration = (Math.random() * 3 + 2) + 's'
        column.style.animationDelay = Math.random() * 2 + 's'
        
        let text = ''
        for (let j = 0; j < 30; j++) {
          text += characters.charAt(Math.floor(Math.random() * characters.length)) + '<br>'
        }
        column.innerHTML = text
        
        matrix.appendChild(column)
      }
    }

    // Create DOM matrix rain
    const domTimeoutId = setTimeout(createDOMMatrixRain, 200)

    // Animate stats counters
    const animateStats = () => {
      const stats = document.querySelectorAll(`.${styles.statNumber}`)
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = parseInt(entry.target.getAttribute('data-target') || '0')
            let current = 0
            const increment = target / 100
            const timer = setInterval(() => {
              current += increment
              if (current >= target) {
                entry.target.textContent = target.toLocaleString()
                clearInterval(timer)
              } else {
                entry.target.textContent = Math.floor(current).toLocaleString()
              }
            }, 20)
            observer.unobserve(entry.target)
          }
        })
      })
      
      stats.forEach(stat => observer.observe(stat))
    }

    animateStats()

    return () => {
      cleanupInterval()
      clearTimeout(domTimeoutId)
    }
  }, [])

  return (
    <div className={styles.potatoPiratesHome}>
      {/* Background Effects */}
      <div className={styles.deepSeaBg}></div>
      <div className={styles.matrixBg} id="matrix"></div>
      <div
        className={styles.glowLayer}
        style={{
          backgroundImage: `
            radial-gradient(circle at ${10 + animationOffset * 0.3}% ${20 + animationOffset * 0.2}%, rgba(0, 255, 136, 0.25) 0%, transparent 25%),
            radial-gradient(circle at ${80 - animationOffset * 0.2}% ${60 + animationOffset * 0.4}%, rgba(0, 204, 255, 0.2) 0%, transparent 20%),
            radial-gradient(circle at ${40 + animationOffset * 0.5}% ${80 - animationOffset * 0.3}%, rgba(0, 255, 136, 0.2) 0%, transparent 18%),
            radial-gradient(circle at ${60 - animationOffset * 0.4}% ${30 + animationOffset * 0.6}%, rgba(0, 204, 255, 0.15) 0%, transparent 15%)
          `,
        }}
      ></div>
      <div className={styles.scanlines}></div>
      <div className={styles.noiseOverlay}></div>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <div className={styles.logo}>POTATO PIRATES</div>
          <ul className={styles.navLinks}>
            <li>
              <Link to="/auth" className={styles.navLoginBtn}>
                LOGIN
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle} data-text="POTATO PIRATES">POTATO PIRATES</h1>
          <p className={styles.heroSubtitle}>The Armada</p>
          <p className={styles.heroDescription}>
            Dive into the ultimate educational adventure where learning meets pirate fun! 
            Navigate through interactive sessions, solve coding challenges 
            and master new skills in this immersive cyber-themed learning experience.
          </p>
          <div className={styles.terminalAccent}>&gt; INITIALIZING_LEARNING_PROTOCOL...</div>
          <div className={styles.ctaButtons}>
            <Link to="/auth" className={`${styles.ctaBtn} ${styles.ctaPrimary}`}>START LEARNING</Link>
          </div>
        </div>
      </section>

      {/* Learning Features Section */}
      <section className={styles.features} id="features">
        <h2 className={styles.featuresTitle}>LEARNING SYSTEMS</h2>
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>🎮</span>
            <h3 className={styles.featureTitle}>GAME-BASED LEARNING</h3>
            <p className={styles.featureDescription}>
              Staying true to our philosophy of learning embedded in gameplay, 
              The Armada bridges the gap between our offline games and online learning.
            </p>
          </div>

          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>👥</span>
            <h3 className={styles.featureTitle}>INTERACTIVE SESSIONS</h3>
            <p className={styles.featureDescription}>
              Engage in real-time interactive learning sessions with gamified activities. 
              From MCQ challenges to coding exercises, every session is an adventure.
            </p>
          </div>

          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>💻</span>
            <h3 className={styles.featureTitle}>CODING ADVENTURES</h3>
            <p className={styles.featureDescription}>
              Master programming through hands-on coding challenges. Learn how to write
              terminal shell commands, conduct packet sniffing and much more!
            </p>
          </div>

          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>🏆</span>
            <h3 className={styles.featureTitle}>ACHIEVEMENT SYSTEM</h3>
            <p className={styles.featureDescription}>
              Unlock badges, climb leaderboards, and track your learning progress. 
              Every completed challenge brings you closer to becoming a master pirate.
            </p>
          </div>

          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>📊</span>
            <h3 className={styles.featureTitle}>REAL TIME ANALYTICS</h3>
            <p className={styles.featureDescription}>
              Get instant feedback on your performance with detailed analytics. 
              Track your strengths, identify areas for improvement, and optimize your learning.
            </p>
          </div>

          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>🎯</span>
            <h3 className={styles.featureTitle}>ADAPTIVE LEARNING</h3>
            <p className={styles.featureDescription}>
              Experience personalized learning paths that adapt to your pace and style. 
              The platform evolves with you, ensuring optimal challenge and engagement.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.howItWorks} id="how">
        <h2 className={styles.featuresTitle}>HOW IT WORKS</h2>

        {/* Role toggle */}
        <div className={styles.roleToggle}>
          <button
            className={`${styles.roleBtn} ${howTab === 'student' ? styles.roleBtnActive : ''}`}
            onClick={() => setHowTab('student')}
          >
            🏴‍☠️ STUDENT
          </button>
          <button
            className={`${styles.roleBtn} ${howTab === 'instructor' ? styles.roleBtnActive : ''}`}
            onClick={() => setHowTab('instructor')}
          >
            ⚓ INSTRUCTOR
          </button>
        </div>

        {howTab === 'student' ? (
          <div className={styles.stepsGrid}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>1</div>
              <h3 className={styles.stepTitle}>JOIN THE CREW</h3>
              <p className={styles.featureDescription}>
                Sign up for an account to browse all the exciting adventures awaiting you!
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>2</div>
              <h3 className={styles.stepTitle}>CHOOSE YOUR ADVENTURE</h3>
              <p className={styles.featureDescription}>
                In each adventure you will learn new skills. Are you up for a challenge, pirate?
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>3</div>
              <h3 className={styles.stepTitle}>COMPLETE MISSIONS</h3>
              <p className={styles.featureDescription}>
                Tackle interactive activities — quizzes, coding challenges, packet analysis, and more — all in real time.
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>4</div>
              <h3 className={styles.stepTitle}>EARN YOUR RANK</h3>
              <p className={styles.featureDescription}>
                See your scores instantly, earn badges, climb the leaderboard, and track your progress across courses.
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.stepsGrid}>
            <div className={styles.stepCard}>
              <div className={`${styles.stepNumber} ${styles.stepNumberInstructor}`}>1</div>
              <h3 className={styles.stepTitle}>ACCESS COURSES</h3>
              <p className={styles.featureDescription}>
                Get access to readymade courses complete with interactive lesson activities
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={`${styles.stepNumber} ${styles.stepNumberInstructor}`}>2</div>
              <h3 className={styles.stepTitle}>SHARE CODE</h3>
              <p className={styles.featureDescription}>
                Start a lesson and share the join code or QR with your students. They can join from any device — no sign-up required.
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={`${styles.stepNumber} ${styles.stepNumberInstructor}`}>3</div>
              <h3 className={styles.stepTitle}>CONTROL THE FLOW</h3>
              <p className={styles.featureDescription}>
                Advance activities, monitor live responses as they come in, and keep the session moving at the right pace. Or let them run it at their own pace.
              </p>
            </div>

            <div className={styles.stepCard}>
              <div className={`${styles.stepNumber} ${styles.stepNumberInstructor}`}>4</div>
              <h3 className={styles.stepTitle}>REVIEW REPORTS</h3>
              <p className={styles.featureDescription}>
                View real-time analytics or download detailed lesson reports with per-student breakdowns, response analytics, and performance insights.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}